import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiJson } from '@/lib/api';
import {
  formatPacificCivilDateLong,
  formatPacificDateTime,
  pacificYmdFromIso,
} from '@/lib/adminPacificTime';
import { formatShortDate } from '@/lib/utils';
import type { AdminJournalFeedItem, AdminJournalFeedResponse } from '@/lib/types';

const PAGE_SIZE = 100;

const ADMIN_USER_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-nova-input text-xs font-semibold text-nova-foreground hover:bg-nova-accent';

function authorLabel(author: AdminJournalFeedItem['author'], userId: number) {
  const n = [author.first_name, author.last_name].filter(Boolean).join(' ').trim();
  return n || `User #${userId}`;
}

type UserBlock = {
  userId: number;
  name: string;
  email: string | null;
  items: AdminJournalFeedItem[];
};

type DayBlock = {
  date: string;
  users: UserBlock[];
};

/**
 * Buckets by Pacific calendar date of `created_at` (when the entry was saved).
 * Newest Pacific days first; within each day, users ordered by their latest save that day.
 */
function buildDayUserBuckets(items: AdminJournalFeedItem[]): DayBlock[] {
  const byDay = new Map<string, AdminJournalFeedItem[]>();
  for (const it of items) {
    const d = pacificYmdFromIso(it.entry.created_at);
    if (!byDay.has(d)) byDay.set(d, []);
    byDay.get(d)!.push(it);
  }
  const dayOrder = [...byDay.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));

  return dayOrder.map((date) => {
    const dayItems = [...byDay.get(date)!].sort(
      (a, b) => new Date(b.entry.created_at).getTime() - new Date(a.entry.created_at).getTime(),
    );
    const userMap = new Map<number, AdminJournalFeedItem[]>();
    for (const it of dayItems) {
      const uid = it.entry.creator_user_id;
      if (!userMap.has(uid)) userMap.set(uid, []);
      userMap.get(uid)!.push(it);
    }
    const userIds = [...userMap.keys()].sort((a, b) => {
      const maxA = Math.max(...userMap.get(a)!.map((x) => new Date(x.entry.created_at).getTime()));
      const maxB = Math.max(...userMap.get(b)!.map((x) => new Date(x.entry.created_at).getTime()));
      return maxB - maxA;
    });
    const users: UserBlock[] = userIds.map((userId) => {
      const raw = userMap.get(userId)!;
      const list = [...raw].sort(
        (x, y) => new Date(y.entry.created_at).getTime() - new Date(x.entry.created_at).getTime(),
      );
      const first = list[0]!;
      return {
        userId,
        name: authorLabel(first.author, userId),
        email: first.author.email,
        items: list,
      };
    });
    return { date, users };
  });
}

function FeedEntryCard({ item }: { item: AdminJournalFeedItem }) {
  const entry = item.entry;
  return (
    <li className='bg-nova-card rounded-lg border border-nova-border shadow-sm p-4 text-sm'>
      <div className='flex flex-wrap items-start justify-between gap-2 border-b border-nova-border pb-2 mb-2'>
        <div className='min-w-0'>
          <p className='font-semibold text-nova-foreground truncate'>
            {entry.title?.trim() ||
              (entry.entry_type === 'PHONE_CALL'
                ? 'Phone call'
                : entry.entry_type === 'EXTERNAL_MESSAGE'
                  ? 'External message'
                  : 'Untitled')}
          </p>
          <p className='text-xs text-nova-muted-foreground mt-0.5'>
            <span className='tabular-nums'>Entry date {formatShortDate(entry.entry_date)}</span>
            <span className='text-nova-muted-foreground'> · </span>
            <span className='font-mono text-nova-muted-foreground'>{entry.entry_type}</span>
            <span className='text-nova-muted-foreground'> · </span>
            <span>#{entry.id}</span>
            <span className='text-nova-muted-foreground'> · </span>
            <span>Household #{entry.household_id}</span>
          </p>
        </div>
        {entry.attachment_count != null && entry.attachment_count > 0 ? (
          <span className='text-xs font-medium text-nova-muted-foreground bg-nova-muted rounded-md px-2 py-0.5 shrink-0'>
            {entry.attachment_count} attachment{entry.attachment_count === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {entry.entry_type === 'PHONE_CALL' ? (
        <p className='text-xs text-nova-muted-foreground mb-2'>
          {[entry.call_direction, entry.call_type].filter(Boolean).join(' · ')}
          {entry.call_duration_minutes != null ? ` · ${entry.call_duration_minutes} min` : null}
        </p>
      ) : null}

      {entry.entry_type === 'EXTERNAL_MESSAGE' ? (
        <p className='text-xs text-nova-muted-foreground mb-2'>
          {[entry.external_channel, entry.external_direction].filter(Boolean).join(' · ')}
          {entry.external_occurred_at
            ? ` · ${formatPacificDateTime(entry.external_occurred_at)}`
            : null}
        </p>
      ) : null}

      {entry.body?.trim() ? (
        <p className='text-nova-foreground whitespace-pre-wrap line-clamp-8'>{entry.body.trim()}</p>
      ) : (
        <p className='text-xs text-nova-muted-foreground italic'>No body text</p>
      )}

      {(entry.tags?.length ?? 0) > 0 ? (
        <div className='flex flex-wrap gap-1.5 mt-3'>
          {entry.tags!.map((t) => (
            <span
              key={t.id}
              className='text-[10px] font-semibold px-2 py-0.5 rounded-md border border-nova-border'
              style={{ color: t.color, borderColor: `${t.color}55` }}
            >
              {t.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className='flex flex-wrap items-center gap-2 mt-3 text-[10px] text-nova-muted-foreground'>
        <span className='tabular-nums'>Saved {formatPacificDateTime(entry.created_at)}</span>
        <Link
          to='/admin/users/$userId/journal'
          params={{ userId: String(entry.creator_user_id) }}
          search={{ hid: String(entry.household_id) }}
          className={ADMIN_USER_LINK_CLASS}
        >
          Full journal
        </Link>
      </div>
    </li>
  );
}

function AdminJournalFeedPage() {
  const { getToken } = useAuth();
  const [page, setPage] = useState(0);

  const feedQuery = useQuery({
    queryKey: ['admin', 'journal-feed', page],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      return apiJson<AdminJournalFeedResponse>(
        getToken,
        `/v1/admin/journal-feed?${params.toString()}`,
      );
    },
    placeholderData: keepPreviousData,
  });

  const buckets = useMemo(() => {
    const raw = feedQuery.data?.entries ?? [];
    const bySave = [...raw].sort(
      (a, b) => new Date(b.entry.created_at).getTime() - new Date(a.entry.created_at).getTime(),
    );
    return buildDayUserBuckets(bySave);
  }, [feedQuery.data?.entries]);

  const total = feedQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const data = feedQuery.data;

  return (
    <div className='space-y-4'>
      <div>
        <h1 className='text-xl font-semibold text-nova-foreground'>Journal feed</h1>
        <p className='text-sm text-nova-muted-foreground mt-1'>
          Grouped by saved date in US Pacific time, then by author. Newest days and newest saves
          appear first. The entry date line is the date the parent chose for the note.
        </p>
      </div>

      {feedQuery.isLoading && !data ? (
        <div className='flex justify-center py-16'>
          <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : feedQuery.isError ? (
        <p className='text-sm text-red-600'>Could not load journal feed.</p>
      ) : buckets.length === 0 ? (
        <p className='text-sm text-nova-muted-foreground bg-nova-card rounded-lg border border-nova-border p-4'>
          No journal entries yet.
        </p>
      ) : (
        <div className='space-y-10'>
          {buckets.map((day) => (
            <section key={day.date} className='space-y-4'>
              <h2 className='text-lg font-semibold text-nova-foreground border-b border-nova-border pb-2'>
                {formatPacificCivilDateLong(day.date)}
                <span className='ml-2 text-sm font-normal text-nova-muted-foreground tabular-nums'>
                  ({day.date} PT)
                </span>
              </h2>
              {day.users.map((u) => (
                <div key={`${day.date}-${u.userId}`} className='space-y-3'>
                  <div className='flex flex-wrap items-baseline justify-between gap-2'>
                    <div>
                      <p className='text-sm font-semibold text-nova-foreground'>{u.name}</p>
                      {u.email ? (
                        <p className='text-xs text-nova-muted-foreground truncate max-w-xl' title={u.email}>
                          {u.email}
                        </p>
                      ) : null}
                    </div>
                    <Link
                      to='/admin/users/$userId'
                      params={{ userId: String(u.userId) }}
                      search={{ hid: undefined }}
                      className={ADMIN_USER_LINK_CLASS}
                    >
                      User profile
                    </Link>
                  </div>
                  <ul className='space-y-3 pl-0 sm:pl-4 border-l-0 sm:border-l-2 border-nova-border sm:ml-1'>
                    {u.items.map((item) => (
                      <FeedEntryCard key={item.entry.id} item={item} />
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}

      {total > 0 && (
        <div className='flex items-center justify-between text-sm text-nova-muted-foreground pt-2'>
          <span className='tabular-nums'>
            Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + (data?.entries.length ?? 0), total)}{' '}
            of {total}
          </span>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || feedQuery.isFetching}
              className='px-3 py-1 border border-nova-input rounded-md disabled:opacity-50'
            >
              Prev
            </button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type='button'
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1 || feedQuery.isFetching}
              className='px-3 py-1 border border-nova-input rounded-md disabled:opacity-50'
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/journal')({
  component: AdminJournalFeedPage,
});
